import React, { useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { toast } from 'react-toastify';
import {
  defaultMappingFormData,
  formatMappingPayload,
  isMappingFieldReadOnly,
  MAPPING_TYPE,
  prepareMappingFormRow,
} from './transformationMappingUiConfig';

const SectionDivider = ({ children }) => (
  <Grid item xs={12}>
    <Divider sx={{ my: 1.5 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.875rem', px: 1 }}>
        {children}
      </Typography>
    </Divider>
  </Grid>
);

const scriptFieldSx = { '& .MuiInputBase-input': { fontFamily: 'monospace', fontSize: '0.8125rem' } };

/** shrink: true keeps outlined labels inside the border (avoids clip in scrollable dialogs) */
const MappingTextField = ({ InputLabelProps, ...props }) => (
  <TextField
    variant="outlined"
    size="small"
    fullWidth
    InputLabelProps={{ shrink: true, ...InputLabelProps }}
    {...props}
  />
);

const TransformationMappingFormDialog = ({
  open,
  onClose,
  mapping,
  editMode,
  type,
  headerCode,
  apiBaseUrl,
  isTenant = false,
  onSave,
}) => {
  const [formData, setFormData] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (mapping) {
      setFormData(prepareMappingFormRow(mapping));
    } else {
      setFormData(defaultMappingFormData(headerCode, type));
    }
  }, [mapping, headerCode, type]);

  const fieldReadOnly = (name) =>
    isMappingFieldReadOnly({ isTenant, editMode, type, fieldName: name });

  const handleInputChange = (e) => {
    const { name, value, type: inputType, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: inputType === 'checkbox' ? checked : value,
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const url = editMode ? `${apiBaseUrl}/${mapping.id}` : apiBaseUrl;
      const method = editMode ? 'PUT' : 'POST';
      const payload = formatMappingPayload(formData, type);

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      toast.success(`Mapping ${editMode ? 'updated' : 'created'} successfully`);
      onSave();
    } catch (error) {
      console.error('Error saving mapping:', error);
      toast.error(`Failed to save mapping: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const label = type === MAPPING_TYPE.HEADER ? 'Header Column' : 'Line Item';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
      PaperProps={{ sx: { maxHeight: '90vh' } }}
    >
      <DialogTitle sx={{ pb: 1, fontSize: '1rem', fontWeight: 600 }}>
        {editMode ? 'Edit' : 'Add'} {label} Mapping
      </DialogTitle>
      <DialogContent dividers sx={{ pt: 2.5, pb: 2 }}>
        <Grid container spacing={2}>
            <Grid item xs={12} sm={type === MAPPING_TYPE.LINE_ITEM ? 6 : 4}>
              <MappingTextField
                label="Header Code"
                name="headerCode"
                value={formData.headerCode || ''}
                onChange={handleInputChange}
                required
                fullWidth
                size="small"
                disabled={editMode || fieldReadOnly('headerCode')}
              />
            </Grid>
            {type === MAPPING_TYPE.LINE_ITEM && (
              <Grid item xs={12} sm={6}>
                <MappingTextField
                  label="Sub Category"
                  name="subCategory"
                  value={formData.subCategory || ''}
                  onChange={handleInputChange}
                  fullWidth
                  size="small"
                  disabled={fieldReadOnly('subCategory')}
                />
              </Grid>
            )}
            <Grid item xs={6} sm={type === MAPPING_TYPE.LINE_ITEM ? 3 : 4}>
              <MappingTextField
                label="Sequence"
                name="sequence"
                type="number"
                value={formData.sequence ?? 0}
                onChange={handleInputChange}
                fullWidth
                size="small"
                inputProps={{ min: 0 }}
                disabled={fieldReadOnly('sequence')}
              />
            </Grid>
            <Grid item xs={6} sm={type === MAPPING_TYPE.LINE_ITEM ? 3 : 4}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="active"
                    checked={formData.active !== false}
                    onChange={handleInputChange}
                    disabled={fieldReadOnly('active')}
                  />
                }
                label="Active"
              />
            </Grid>

            <SectionDivider>Pre-Source Fields</SectionDivider>

            <Grid item xs={12} sm={6}>
              <MappingTextField
                label="Pre Source Field Name"
                name="preSourceFieldName"
                value={formData.preSourceFieldName || ''}
                onChange={handleInputChange}
                fullWidth
                size="small"
                disabled={fieldReadOnly('preSourceFieldName')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MappingTextField
                label="Pre Source JSON Node"
                name="preSourceJsonNode"
                value={formData.preSourceJsonNode || ''}
                onChange={handleInputChange}
                fullWidth
                size="small"
                placeholder="e.g., $.customer.name"
                disabled={fieldReadOnly('preSourceJsonNode')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MappingTextField
                label="Pre Source Field Type"
                name="preSourceFieldType"
                value={formData.preSourceFieldType || ''}
                onChange={handleInputChange}
                fullWidth
                size="small"
                disabled={fieldReadOnly('preSourceFieldType')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MappingTextField
                label="Pre Source Default Data"
                name="preSourceDefaultData"
                value={formData.preSourceDefaultData || ''}
                onChange={handleInputChange}
                fullWidth
                size="small"
                disabled={fieldReadOnly('preSourceDefaultData')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MappingTextField
                label="Pre Source Static Data"
                name="preSourceStaticData"
                value={formData.preSourceStaticData || ''}
                onChange={handleInputChange}
                fullWidth
                size="small"
                disabled={fieldReadOnly('preSourceStaticData')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="isPreSourceDate"
                    checked={!!formData.isPreSourceDate}
                    onChange={handleInputChange}
                    disabled={fieldReadOnly('isPreSourceDate')}
                  />
                }
                label="Is Pre-Source Date"
              />
            </Grid>
            <Grid item xs={12}>
              <MappingTextField
                label="Pre Transform Script"
                name="preTransformScript"
                value={formData.preTransformScript || ''}
                onChange={handleInputChange}
                multiline
                minRows={6}
                maxRows={20}
                fullWidth
                size="small"
                placeholder="Groovy script for pre-transformation"
                sx={scriptFieldSx}
                disabled={fieldReadOnly('preTransformScript')}
              />
            </Grid>

            <SectionDivider>Source Fields</SectionDivider>

            <Grid item xs={12} sm={6}>
              <MappingTextField
                label="Source Field Name"
                name="sourceFieldName"
                value={formData.sourceFieldName || ''}
                onChange={handleInputChange}
                fullWidth
                size="small"
                disabled={fieldReadOnly('sourceFieldName')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MappingTextField
                label="Source Field Type"
                name="sourceFieldType"
                value={formData.sourceFieldType || ''}
                onChange={handleInputChange}
                fullWidth
                size="small"
                disabled={fieldReadOnly('sourceFieldType')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MappingTextField
                label="Source Default Data"
                name="sourceDefaultData"
                value={formData.sourceDefaultData || ''}
                onChange={handleInputChange}
                fullWidth
                size="small"
                disabled={fieldReadOnly('sourceDefaultData')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MappingTextField
                label="Source Static Data"
                name="sourceStaticData"
                value={formData.sourceStaticData || ''}
                onChange={handleInputChange}
                fullWidth
                size="small"
                disabled={fieldReadOnly('sourceStaticData')}
              />
            </Grid>
            <Grid item xs={12}>
              <MappingTextField
                label="Post Transform Script"
                name="postTransformScript"
                value={formData.postTransformScript || ''}
                onChange={handleInputChange}
                multiline
                minRows={6}
                maxRows={20}
                fullWidth
                size="small"
                placeholder="Groovy script for post-transformation"
                sx={scriptFieldSx}
                disabled={fieldReadOnly('postTransformScript')}
              />
            </Grid>

            <SectionDivider>Target &amp; Control</SectionDivider>

            <Grid item xs={12} sm={6}>
              <MappingTextField
                label="Target Column"
                name="targetColumn"
                value={formData.targetColumn || ''}
                onChange={handleInputChange}
                fullWidth
                size="small"
                disabled={fieldReadOnly('targetColumn')}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <MappingTextField
                label="Target Width"
                name="targetWidth"
                type="number"
                value={formData.targetWidth ?? ''}
                onChange={handleInputChange}
                fullWidth
                size="small"
                inputProps={{ min: 0 }}
                disabled={fieldReadOnly('targetWidth')}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <MappingTextField
                label="Target Data Type"
                name="targetDataType"
                value={formData.targetDataType || ''}
                onChange={handleInputChange}
                fullWidth
                size="small"
                disabled={fieldReadOnly('targetDataType')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MappingTextField
                label="API ID (UUID)"
                name="apiId"
                value={formData.apiId || ''}
                onChange={handleInputChange}
                fullWidth
                size="small"
                placeholder="Links to Data Hub API config"
                disabled={fieldReadOnly('apiId')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MappingTextField
                label="Non-Allowed Characters"
                name="nonAllowedCharacters"
                value={formData.nonAllowedCharacters || ''}
                onChange={handleInputChange}
                fullWidth
                size="small"
                disabled={fieldReadOnly('nonAllowedCharacters')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    name="isSourceSystemId"
                    checked={!!formData.isSourceSystemId}
                    onChange={handleInputChange}
                    disabled={fieldReadOnly('isSourceSystemId')}
                  />
                }
                label="Is Source System ID"
              />
            </Grid>
            {type === MAPPING_TYPE.HEADER && !isTenant && (
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      name="storeForParentIteration"
                      checked={!!formData.storeForParentIteration}
                      onChange={handleInputChange}
                    />
                  }
                  label="Store for Parent Iteration"
                />
              </Grid>
            )}

            <SectionDivider>Mapping Lookup (optional)</SectionDivider>

            <Grid item xs={12} sm={6}>
              <MappingTextField
                label="Mapping Table"
                name="mappingTable"
                value={formData.mappingTable || ''}
                onChange={handleInputChange}
                fullWidth
                size="small"
                placeholder="e.g., budget_mapping"
                disabled={fieldReadOnly('mappingTable')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MappingTextField
                label="Mapping Target App"
                name="mappingTargetApp"
                value={formData.mappingTargetApp || ''}
                onChange={handleInputChange}
                fullWidth
                size="small"
                disabled={fieldReadOnly('mappingTargetApp')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MappingTextField
                label="Mapping Source Column"
                name="mappingSourceColumn"
                value={formData.mappingSourceColumn || ''}
                onChange={handleInputChange}
                fullWidth
                size="small"
                disabled={fieldReadOnly('mappingSourceColumn')}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <MappingTextField
                label="Mapping Target Column"
                name="mappingTargetColumn"
                value={formData.mappingTargetColumn || ''}
                onChange={handleInputChange}
                fullWidth
                size="small"
                disabled={fieldReadOnly('mappingTargetColumn')}
              />
            </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={saving}>
          {saving ? 'Saving...' : editMode ? 'Update' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransformationMappingFormDialog;
